import { FormattedMessage } from 'react-intl';
import { ofType } from 'redux-observable';
import { EMPTY } from 'rxjs';
import { mergeMap, catchError, filter, take } from 'rxjs/operators';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import * as Actions from './purchase-orders.actions';
import * as PurchaseOrdersApi from './purchase-orders.api';
import { messages } from '../../pages/products-page/purchase-orders/messages';
import { DocumentService } from '../../services/document-service';

import type { FilterBarFilter, SortState } from './purchase-orders.types';
import type { Observable } from 'rxjs';

const NOTIFICATION_DURATION_IN_MS = 5000;

const exportPurchaseOrdersAsync = (
  action$: Observable<Actions.PurchaseOrdersAction>,
  appliedFilters: FilterBarFilter[],
  sortState: SortState,
  purchaseOrderIds: string[] | null,
): Observable<never> => {
  return PurchaseOrdersApi.exportPurchaseOrdersAsync(appliedFilters, sortState, purchaseOrderIds).pipe(
    mergeMap((jobId: string) => {
      const exportComplete$ = action$.pipe(
        ofType(Actions.EXPORT_PURCHASE_ORDERS_ASYNC_SUCCESS),
        filter((response): response is Actions.ExportPurchaseOrdersAsyncSuccessAction => {
          return (
            response.type === Actions.EXPORT_PURCHASE_ORDERS_ASYNC_SUCCESS && 'messageGuid' in response && response.messageGuid === jobId
          );
        }),
        take(1),
        mergeMap((response) => {
          const downloadUrl = response?.data?.DownloadUrl;
          if (downloadUrl) {
            DocumentService.downloadFile(downloadUrl);
          }
          return EMPTY;
        }),
      );
      return exportComplete$;
    }),
    catchError(() => {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...messages.purchaseOrderExportFailureNotificationTitle} />,
        content: <FormattedMessage {...messages.purchaseOrderExportFailureNotificationContent} />,
        duration: NOTIFICATION_DURATION_IN_MS,
      });
      return EMPTY;
    }),
  );
};

export const exportPurchaseOrdersRequest = (action$: Observable<Actions.PurchaseOrdersAction>): Observable<never> => {
  return action$.pipe(
    ofType(Actions.EXPORT_PURCHASE_ORDERS_REQUEST),
    mergeMap((action) => {
      const typedAction = action as Actions.ExportPurchaseOrdersRequestAction;
      const { appliedFilters, sortState, purchaseOrderIds } = typedAction;
      return exportPurchaseOrdersAsync(action$, appliedFilters, sortState, purchaseOrderIds);
    }),
  );
};
